import { Checkbox } from 'antd';
import { useController, useFormContext } from 'react-hook-form';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import { AccessDurationMode } from '../../constants';
import type { UserFormValues } from '../../../../types/UserManagementTypes';
import { ExpiryDatePicker, InfoIcon, PasswordInputField } from './fields';
import {
  ACCESS_DURATION_OPTIONS,
  FIELD_LABELS,
  FIELD_PLACEHOLDERS,
  SECURITY_STEP_NOTE,
  STEP_HEADINGS,
  accessDurationPopover,
  passwordRequirementsPopover,
} from './constants';
import styles from './UserFormModal.module.scss';

const SecurityStep = () => {
  const { field: durationMode, fieldState: durationState } = useController<UserFormValues, 'accessDurationMode'>({
    name: 'accessDurationMode',
  });
  const { field: expiryDate, fieldState: expiryState } = useController<UserFormValues, 'accessExpiryDate'>({ name: 'accessExpiryDate' });
  const { clearErrors } = useFormContext<UserFormValues>();

  const isSpecificDate = durationMode.value === AccessDurationMode.SPECIFIC_DATE;

  // Exclusive, so picking one clears the other and any date it left behind.
  const selectMode = (mode: AccessDurationMode) => {
    clearErrors(['accessDurationMode', 'accessExpiryDate']);
    durationMode.onChange(mode);
    if (mode !== AccessDurationMode.SPECIFIC_DATE) expiryDate.onChange(null);
  };

  return (
    <section className={styles.step}>
      <h3 className={styles.stepHeading}>
        {STEP_HEADINGS.SECURITY}
        <InfoIcon content={passwordRequirementsPopover} />
      </h3>
      <p className={styles.stepNote}>{SECURITY_STEP_NOTE}</p>

      <PasswordInputField name="password" label={FIELD_LABELS.PASSWORD} placeholder={FIELD_PLACEHOLDERS.PASSWORD} />
      <PasswordInputField name="confirmPassword" label={FIELD_LABELS.CONFIRM_PASSWORD} placeholder={FIELD_PLACEHOLDERS.CONFIRM_PASSWORD} />

      <h3 className={styles.stepHeading}>
        {STEP_HEADINGS.ACCESS_DURATION}
        <InfoIcon content={accessDurationPopover} />
      </h3>
      <div className={styles.durationGroup} role="group" aria-label={STEP_HEADINGS.ACCESS_DURATION}>
        {ACCESS_DURATION_OPTIONS.map((option) => (
          <div key={option.value} className={styles.durationRow}>
            <Checkbox className="custom-checkbox" checked={durationMode.value === option.value} onChange={() => selectMode(option.value)}>
              <span className={styles.optionLabel}>{option.label}</span>
            </Checkbox>
            <ConditionalRender visible={option.value === AccessDurationMode.SPECIFIC_DATE} hideFallback>
              <ExpiryDatePicker name="accessExpiryDate" disabled={!isSpecificDate} />
            </ConditionalRender>
          </div>
        ))}
      </div>
      <span className={styles.errorSlot}>{durationState.error?.message ?? expiryState.error?.message}</span>
    </section>
  );
};

export default SecurityStep;
